import styles from './styles.module.css'
import TechInputColumn from '../tech-input-column/TechInputColumn'
import TechInputBar from '../tech-input-bar/TechInputBar'
import WorkingDaysTable from '../working-days-table/WorkingDaysTable'
import UniqueDaySettingsTable from '../unique-day-settings-table/UniqueDaySettingsTable'
import { ITechnicianPartial } from '../../../../../../../app/entities/Technician'
import { Dispatch, SetStateAction, useState } from 'react'
import { IWeekCalendarPartial } from '../../../../../../../app/entities/WeekCalendar'
import { ILocation } from '../../../../../../../app/entities/Location'
import { Button } from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import DaySettingsManagementModal from '../../../../../locations/components/location-modal/components/day-settings-management-modal/DaySettingsManagementModal'

interface ITechnicianModalContentProps {
  technicianData: ITechnicianPartial
  setTechnicianData: Dispatch<SetStateAction<ITechnicianPartial>>
  isDefaultWorkDay: boolean
  setIsDefaultWorkDay: Dispatch<SetStateAction<boolean>>
  isDefaultLunch: boolean
  setIsDefaultLunch: Dispatch<SetStateAction<boolean>>
  refetchTechnicianData: () => void
  technicianObjectId: string | undefined
  weekCalendars: IWeekCalendarPartial[]
  setWeekCalendars: Dispatch<SetStateAction<IWeekCalendarPartial[]>>
  location: ILocation | null
  setLocation: Dispatch<SetStateAction<ILocation | null>>
  originalLocation: ILocation | null
}

function TechnicianModalContent({
  technicianData,
  setTechnicianData,
  isDefaultLunch,
  setIsDefaultLunch,
  isDefaultWorkDay,
  setIsDefaultWorkDay,
  refetchTechnicianData,
  technicianObjectId,
  weekCalendars,
  setWeekCalendars,
  location,
  setLocation,
  originalLocation,
}: ITechnicianModalContentProps) {
  const [isDaySettingsModalOpen, setIsDaySettingsModalOpen] = useState(false)

  // Check if location is converted to DaySettings-only mode
  // For existing technicians: use their originally loaded location's mode (not currently selected one)
  // For new technicians: originalLocation will be null, falls back to currently selected location
  const isConvertedLocation = (originalLocation ?? location)?.daySettingsOnlyMode === true

  // Check if we're creating a new technician (no objectId yet)
  const isCreatingNewTechnician = !technicianObjectId

  return (
    <div className={styles.mainContainer}>
      <TechInputColumn
        technicianData={technicianData}
        setTechnicianData={setTechnicianData}
        isDefaultWorkDay={isDefaultWorkDay}
        setIsDefaultWorkDay={setIsDefaultWorkDay}
        isDefaultLunch={isDefaultLunch}
        setIsDefaultLunch={setIsDefaultLunch}
        location={location}
      />
      <div className={styles.tableContainer}>
        <TechInputBar
          technicianData={technicianData}
          setTechnicianData={setTechnicianData}
          location={location}
          setLocation={setLocation}
        />

        {!location ? (
          // No location selected yet
          <div className={styles.locationPromptSection}>
            <div className={styles.sectionHeader}>
              <h3>Schedule Configuration</h3>
              <p>Please select a location to configure scheduling.</p>
            </div>
          </div>
        ) : isConvertedLocation && isCreatingNewTechnician ? (
          // Creating new technician in converted location
          <div className={styles.convertedLocationSection}>
            <div className={styles.sectionHeader}>
              <h3>Schedule Management</h3>
              <p>This technician will be created with no working days. You can configure their schedule after creation.</p>
            </div>
          </div>
        ) : isConvertedLocation ? (
          // Editing existing technician in converted location
          <div className={styles.convertedLocationSection}>
            <div className={styles.sectionHeader}>
              <h3>Schedule Management</h3>
              <p>This location has been converted to the new scheduling system.</p>
            </div>
            <Button
              variant="contained"
              startIcon={<CalendarMonthIcon />}
              onClick={() => setIsDaySettingsModalOpen(true)}
              className={styles.manageScheduleButton}
            >
              Manage Schedule
            </Button>
          </div>
        ) : (
          // Legacy mode: Show WeekCalendar + DaySettings table
          <>
            <WorkingDaysTable
              weekCalendars={weekCalendars}
              setWeekCalendars={setWeekCalendars}
            />
            <UniqueDaySettingsTable
              technicianData={technicianData}
              isDefaultWorkDay={isDefaultWorkDay}
              isDefaultLunch={isDefaultLunch}
              refetchTechnicianData={refetchTechnicianData}
              technicianObjectId={technicianObjectId}
            />
          </>
        )}
      </div>

      {/* DaySettings Management Modal */}
      {isConvertedLocation && !isCreatingNewTechnician && (
        <DaySettingsManagementModal
          isOpen={isDaySettingsModalOpen}
          onClose={() => setIsDaySettingsModalOpen(false)}
          locationObjectId={location?.objectId || ''}
          technicianObjectId={technicianObjectId}
        />
      )}
    </div>
  )
}

export default TechnicianModalContent
